import { post } from "./client";
import { type MyCommentReaction, type ShowroomComment } from "./comment";
import { type Nominal, type OkResponse, type TypedUrl, url } from "./common";
import { type PublicShowroom } from "./publicShowroom";
import { type SmallTeamId, type SmallTeamMember } from "./smallTeam";
import { type SparkId } from "./spark";
import { type WorkshopId } from "./workshop";

// ---- IDs ----

export type ShowroomEntryId = Nominal<string, "ShowroomEntry">;

// ---- Form data ----

// Showroom entry form values. Keys are dynamic (driven by each spark's
// projectFormDefinition.fields) plus a fixed set of special fields handled
// specially by the API (title, upload, coverImage, tags, type, description,
// workshopFlags). Per-field shape varies by ProjectFormField.type.
type ShowroomEntryFormData = Record<string, unknown>;

// ---- Entity ----

export type ShowroomEntry = {
  id: ShowroomEntryId;
  smallTeamId: SmallTeamId;
  workshopId: WorkshopId;
  agreedOnFutureUse: boolean;
  commentCount: number;
  reactionCounts: { [emoji: string]: number };
  fitForPublic: boolean | null;
  visitCounts: number;
  publicShowroomId: PublicShowroom["id"] | null;
  createdAt: Date;
  formData: ShowroomEntryFormData;
};

// ---- Response shapes ----

// Item in GET /showroom-entries — subset of ShowroomEntry plus populated team members.
// Excludes agreedOnFutureUse, fitForPublic, publicShowroomId. The formData is
// post-processed by `formDataWithLabel` (each non-special field gets {data, label, type}).
export type ShowroomEntryListItem = {
  id: ShowroomEntryId;
  workshopId: WorkshopId;
  smallTeamId: SmallTeamId;
  formData: ShowroomEntryFormData;
  commentCount: number;
  reactionCounts: { [emoji: string]: number };
  visitCounts: number;
  createdAt: Date;
  teamMembers: SmallTeamMember[];
};

// Returned by GET /teams/:smallTeamId/showroom-entry. Engagement fields are
// always present in the response shape; they default to null/empty when
// `includeEngagement` is not set or the user is not authenticated.
export type SmallTeamShowroomEntryResponse = {
  entry: ShowroomEntry | null;
  publicShowroom: PublicShowroom | null;
  myReaction: string | null;
  comments: ShowroomComment[];
  myCommentReactions: MyCommentReaction[];
};

// ---- Models ----

export const showroomModels = {
  WORKSHOP_SHOWROOM_ENTRIES: (workshopId: WorkshopId) =>
    url<ShowroomEntryListItem[]>(`/showroom-entries?workshop=${workshopId}`),
  SPARK_SHARED_SHOWROOM_ENTRIES: (sparkId: SparkId) =>
    url<ShowroomEntryListItem[]>(`/showroom-entries?spark=${sparkId}`),
  // Same response shape regardless of includeEngagement — the engagement fields
  // are always present, just empty/null when not requested.
  SMALL_TEAM_SHOWROOM_ENTRY: (
    smallTeamId: SmallTeamId,
    includeEngagement?: boolean,
  ): TypedUrl<SmallTeamShowroomEntryResponse> =>
    url<SmallTeamShowroomEntryResponse>(
      `/teams/${smallTeamId}/showroom-entry${includeEngagement ? "?includeEngagement=true" : ""}`,
    ),
};

// ---- Mutations ----

type SetShowroomEntryInput = {
  smallTeamId: SmallTeamId;
  workshopId: WorkshopId;
  formData: ShowroomEntryFormData;
};

type ReactionInput = {
  entryId: ShowroomEntryId;
  reaction: string;
  smallTeamId: SmallTeamId;
  workshopId: WorkshopId;
  sparkId?: SparkId;
};

export const showroomMutations = {
  setShowroomEntry: ({
    smallTeamId,
    workshopId,
    formData,
  }: SetShowroomEntryInput) =>
    post<OkResponse>(
      `/teams/${smallTeamId}/showroom-entry`,
      { smallTeamId, formData },
      [
        showroomModels.SMALL_TEAM_SHOWROOM_ENTRY(smallTeamId),
        showroomModels.WORKSHOP_SHOWROOM_ENTRIES(workshopId),
      ],
    ),
  addReaction: ({
    entryId,
    reaction,
    smallTeamId,
    workshopId,
    sparkId,
  }: ReactionInput) =>
    post<OkResponse>(`/showroom-entry/${entryId}/add-reaction`, { reaction }, [
      showroomModels.SMALL_TEAM_SHOWROOM_ENTRY(smallTeamId),
      showroomModels.SMALL_TEAM_SHOWROOM_ENTRY(smallTeamId, true),
      showroomModels.WORKSHOP_SHOWROOM_ENTRIES(workshopId),
      ...(sparkId
        ? [showroomModels.SPARK_SHARED_SHOWROOM_ENTRIES(sparkId)]
        : []),
    ]),
  removeReaction: ({
    entryId,
    reaction,
    smallTeamId,
    workshopId,
    sparkId,
  }: ReactionInput) =>
    post<OkResponse>(
      `/showroom-entry/${entryId}/remove-reaction`,
      { reaction },
      [
        showroomModels.SMALL_TEAM_SHOWROOM_ENTRY(smallTeamId),
        showroomModels.SMALL_TEAM_SHOWROOM_ENTRY(smallTeamId, true),
        showroomModels.WORKSHOP_SHOWROOM_ENTRIES(workshopId),
        ...(sparkId
          ? [showroomModels.SPARK_SHARED_SHOWROOM_ENTRIES(sparkId)]
          : []),
      ],
    ),
  addVisit: ({
    entryId,
    smallTeamId,
    workshopId,
    sparkId,
  }: {
    entryId: ShowroomEntryId;
    smallTeamId: SmallTeamId;
    workshopId: WorkshopId;
    sparkId?: SparkId;
  }) =>
    post<OkResponse>(`/showroom-entry/${entryId}/visit`, {}, [
      showroomModels.SMALL_TEAM_SHOWROOM_ENTRY(smallTeamId),
      showroomModels.SMALL_TEAM_SHOWROOM_ENTRY(smallTeamId, true),
      showroomModels.WORKSHOP_SHOWROOM_ENTRIES(workshopId),
      ...(sparkId
        ? [showroomModels.SPARK_SHARED_SHOWROOM_ENTRIES(sparkId)]
        : []),
    ]),
};
